import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp, Users, FileText, Target, Loader2, ArrowUp, ArrowDown
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AnalyticsDashboard = () => {
  const [scenarioAnalytics, setScenarioAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/analytics/scenarios`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScenarioAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-12 h-12 text-ssdp-teal animate-spin" />
      </div>
    );
  }

  const scenarios = scenarioAnalytics?.scenarios || [];
  
  // Top performing scenarios
  const topScenarios = [...scenarios].sort((a, b) => b.completion_rate - a.completion_rate).slice(0, 5);
  
  // Most popular (started)
  const popularScenarios = [...scenarios].sort((a, b) => b.started - a.started).slice(0, 5);
  
  // Needs attention (low completion)
  const needsAttention = [...scenarios]
    .filter(s => s.started > 10)
    .sort((a, b) => a.completion_rate - b.completion_rate)
    .slice(0, 5);

  const totalStarts = scenarios.reduce((sum, s) => sum + s.started, 0);
  const totalCompletions = scenarios.reduce((sum, s) => sum + s.completed, 0);
  const avgCompletionRate = totalStarts > 0 ? (totalCompletions / totalStarts * 100).toFixed(1) : 0;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Analytics Dashboard
        </h1>
        <p className="text-gray-600">
          Scenario performance insights and user engagement metrics
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm opacity-90">Total Scenarios</p>
              <p className="text-3xl font-bold">{scenarios.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm opacity-90">Total Starts</p>
              <p className="text-3xl font-bold">{totalStarts.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Target className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm opacity-90">Avg Completion Rate</p>
              <p className="text-3xl font-bold">{avgCompletionRate}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Top Performing Scenarios */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-6">
            <TrendingUp className="w-5 h-5 text-green-600" />
            <h2 className="text-xl font-bold text-gray-900">
              Top Performing Scenarios
            </h2>
          </div>

          <div className="space-y-4">
            {topScenarios.map((scenario, index) => (
              <div key={scenario.id} className="relative">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className="text-lg font-bold text-ssdp-teal flex-shrink-0">
                      #{index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">
                        {scenario.title}
                      </p>
                      <p className="text-xs text-gray-500">{scenario.category}</p>
                    </div>
                  </div>
                  <span className="text-sm font-bold text-green-600 flex-shrink-0 ml-2">
                    {scenario.completion_rate}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-green-500 to-emerald-600 h-2 rounded-full transition-all"
                    style={{ width: `${scenario.completion_rate}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {scenario.completed} of {scenario.started} completed
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Most Popular Scenarios */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-6">
            <Users className="w-5 h-5 text-purple-600" />
            <h2 className="text-xl font-bold text-gray-900">
              Most Popular Scenarios
            </h2>
          </div>

          <div className="space-y-4">
            {popularScenarios.map((scenario, index) => (
              <div key={scenario.id}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className="text-lg font-bold text-purple-600 flex-shrink-0">
                      #{index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">
                        {scenario.title}
                      </p>
                      <p className="text-xs text-gray-500">{scenario.difficulty}</p>
                    </div>
                  </div>
                  <span className="text-sm font-bold text-purple-600 flex-shrink-0 ml-2">
                    {scenario.started}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-purple-500 to-indigo-600 h-2 rounded-full transition-all"
                    style={{ width: `${(scenario.started / popularScenarios[0].started * 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Needs Attention */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-6">
          <ArrowDown className="w-5 h-5 text-red-600" />
          <h2 className="text-xl font-bold text-gray-900">
            Scenarios Needing Attention
          </h2>
          <span className="text-sm text-gray-500 ml-2">(Low completion rates)</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                  Scenario
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                  Category
                </th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">
                  Started
                </th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">
                  Completed
                </th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">
                  Rate
                </th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-900">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {needsAttention.map((scenario) => (
                <tr key={scenario.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <p className="font-semibold text-gray-900">{scenario.title}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600">{scenario.category}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-sm font-medium">{scenario.started}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-sm font-medium">{scenario.completed}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                      scenario.completion_rate < 30 
                        ? 'bg-red-100 text-red-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {scenario.completion_rate}%
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => window.location.href = `/admin/scenarios/${scenario.id}/edit`}
                      className="text-sm text-ssdp-teal hover:text-indigo-800 font-medium"
                    >
                      Review & Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {needsAttention.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">All scenarios are performing well! 🎉</p>
          </div>
        )}
      </div>

      {/* Recommendations */}
      <div className="mt-6 bg-indigo-50 border-2 border-indigo-200 rounded-2xl p-6">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">
          💡 Recommendations
        </h3>
        <ul className="space-y-2 text-sm text-indigo-800">
          <li className="flex items-start gap-2">
            <span className="flex-shrink-0">•</span>
            <span>Scenarios with completion rates below 50% may need clearer instructions or shorter length</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="flex-shrink-0">•</span>
            <span>High-performing scenarios can be used as templates for new content</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="flex-shrink-0">•</span>
            <span>Consider promoting popular scenarios to new users to increase engagement</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
